import { useCampaign } from '../contexts/CampaignContext/useCampaign';
import { useTranslation } from 'react-i18next';
import { getCurrentLanguage } from '../utils/languageUtils';

/**
 * Hook to calculate promotional pricing for services
 * @param {string} serviceId - Service identifier
 * @param {number|string} basePrice - Base price (can be number or string with "лв")
 * @param {number} squareMeters - Square meters (optional, for packages)
 * @returns {Object} Price calculation details
 */
export const useCampaignPrice = (serviceId, basePrice, squareMeters = null) => {
  const { campaign, isCampaignActive, appliesDiscount } = useCampaign();
  const { i18n } = useTranslation();
  const currentLanguage = getCurrentLanguage(i18n);

  // Parse base price from string if needed
  const parsedBasePrice =
    typeof basePrice === 'string'
      ? parseFloat(basePrice.replace(/[^\d.]/g, ''))
      : parseFloat(basePrice);

  // Default return if no campaign or invalid price
  if (
    !isCampaignActive ||
    !campaign ||
    isNaN(parsedBasePrice) ||
    !appliesDiscount(serviceId, squareMeters)
  ) {
    return {
      displayPrice: basePrice,
      hasDiscount: false,
      originalPrice: parsedBasePrice,
      discountedPrice: parsedBasePrice,
      discountPercent: 0,
      discountNote: null,
    };
  }

  const serviceDiscount = campaign.discounts[serviceId];

  if (!serviceDiscount) {
    return {
      displayPrice: basePrice,
      hasDiscount: false,
      originalPrice: parsedBasePrice,
      discountedPrice: parsedBasePrice,
      discountPercent: 0,
      discountNote: null,
    };
  }

  // Check minSquareMeters condition
  if (serviceDiscount.minSquareMeters && squareMeters) {
    if (squareMeters < serviceDiscount.minSquareMeters) {
      return {
        displayPrice: basePrice,
        hasDiscount: false,
        originalPrice: parsedBasePrice,
        discountedPrice: parsedBasePrice,
        discountPercent: 0,
        discountNote: null,
      };
    }
  }

  // Calculate discount
  const discountPercent = serviceDiscount.percent;
  const discountedPrice = parsedBasePrice * (1 - discountPercent / 100);

  // Extract discount note for current language
  const discountNote = serviceDiscount.note
    ? serviceDiscount.note[currentLanguage] || serviceDiscount.note.bg
    : null;

  return {
    displayPrice: discountedPrice,
    hasDiscount: true,
    originalPrice: parsedBasePrice,
    discountedPrice: discountedPrice,
    discountPercent: discountPercent,
    discountNote: discountNote,
  };
};
